import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, NgForm } from '@angular/forms';
import {
    BlobType,
    Dataset,
    DatasetFileTypeOption,
    FileColumnKey,
    FileModel,
    Folder,
    ItemsToBeRemovedOptions,
    ListSearch,
    MEDFileColumnKeys,
    MlModel,
    NameAndSelectedFilesContinueInformation,
    ValidAndInvalidFiles,
} from '@ca/core/models';
import {
    ExperimentalFeaturesService,
    FilePreviewService,
    FilesValidatorService,
    FileUploadService,
    Message,
    MessagingService,
    TrackingService,
    UserNotificationService,
    UserService,
    WindowRef,
} from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Guid } from 'guid-typescript';
import { cloneDeep } from 'lodash';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, EMPTY, iif, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { CreateFolder } from 'src/app/core/models/create-folder.model';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import { getNewDateFormatted } from 'src/utils/date/date-utils';
import * as FileUtils from '../../../../utils/file/file-utils';
import { CZI_EXTENSION } from '../../constants/constants';
import { CZI_CONVERSION_DONE_NEXT_ANNOTATE_DATASET, CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE } from '../../constants/czi-conversion-messages';
import { RemoveInvalidFilesDialogComponent } from '../remove-invalid-files-dialog/remove-invalid-files-dialog.component';
import { SupportedFormatsDialogComponent } from '../supported-formats-dialog/supported-formats-dialog.component';
import { CreateAndStartFacadeService } from './services/create-and-start-facade/create-and-start-facade.service';

@UntilDestroy()
@Component({
    selector: 'ca-create-and-start-dialog',
    templateUrl: './create-and-start-dialog.component.html',
    styleUrls: ['./create-and-start-dialog.component.scss'],
    providers: [CreateAndStartFacadeService],
})
export class CreateAndStartDialogComponent implements AfterViewInit, OnInit {
    targetFolderToBeCreated: CreateFolder;
    applicationName: string;
    currentPreselectedDataset: Dataset;
    datasetFileTypeOptionEnum = DatasetFileTypeOption;
    name = '';
    searchAndSortOptionsForFileSelection: ListSearch = {
        pageNumber: 1,
        pageSize: 10,
        searchQuery: '',
        sortBy: '',
    };
    uploader: FileUploader | null;
    isRunButtonVisible = true;
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;
    areAllSelectedFilesOfTypeCZI = false;
    isFileExplorerShown = false;
    isFilesStatusShown = false;
    blobType = BlobType.UserFile;
    cancelUpload: FileItem;
    cancelAllUploads: { cancel: boolean };
    isValidationInProgress?: boolean;

    private _parentFolderName: string;
    private uploadFolder: Folder;
    private _selectedFiles: FileModel[] = [];
    private _supportedFileTypes = FileUtils.machineLearningSupportedFileTypes;
    private conversionStatusChecked = false;
    private updateFileStatuesAction$$ = new BehaviorSubject('');
    readonly uploadMaxFileSizeInBytes = environment.machineLearningMaxFileSizeInBytes;

    @ViewChild('nameForm') nameForm: NgForm;
    @Input() datasetExplorerFolderHandlerType?: DatasetFileTypeOption;
    @Input() operationId: string;
    @Input() continueButtonTranslationKey: string;
    @Input() isCziConversionActivatedForUploadedFiles = true;
    @Input() messagingTopicId = Guid.create().toString();
    @Input() modalTitleTranslationKey: string;
    @Input() nameLabelTranslationKey: string;
    @Input() namePlaceholderTranslationKey: string;
    @Input() shouldNameFormBeShown = true;
    @Input() shouldCheckIfNameExistsAsFolderAndRenameFolder = this.shouldNameFormBeShown && true;
    @Input() shouldCheckValidityOfName = this.shouldNameFormBeShown && true;
    @Input() shouldContinueButtonBeValidated = true;
    @Input() isSelectedFilesShown?: boolean;
    @Input() isUploadOfFilesEnabled?: boolean = true;
    @Input() isMEDCZIDisabled?: boolean = false;
    @Input() isSegmentation?: boolean;
    @Input() columns?: FileColumn<FileColumnKey | MEDFileColumnKeys>[];
    @Input() selectedModel?: MlModel | null = null;
    @Input() set parentFolderName(parentFolderName: string) {
        this._parentFolderName = parentFolderName;
        this.applicationName = this._parentFolderName;
        this.targetFolderToBeCreated = {
            subfolderNames: [this.parentFolderName, `run ${getNewDateFormatted('DD.MM.YYYY | HH:mm:ss')}`],
        };
    }
    // STORE INTO DATASET
    @Input() set preselectedDataset(dataset: Dataset) {
        // please do not change the dataset that comes in else the dataset in the inputting component would also be changed
        this.currentPreselectedDataset = cloneDeep(dataset);
    }
    @Input() set supportedFileTypes(supportedFileTypes: string[]) {
        this._supportedFileTypes = [...supportedFileTypes];
    }
    @Input() set selectedFiles(selectedFiles: FileModel[]) {
        if (selectedFiles != null) {
            this._selectedFiles = selectedFiles;
            this.areAllSelectedFilesOfTypeCZI = this.areAllFilesOfTypeCZI(selectedFiles);

            if (this.currentPreselectedDataset && !this.conversionStatusChecked) {
                this.conversionStatusChecked = true;
                this.updateFileStatuesAction$$.next(Guid.create().toString());
            }
        }
    }

    @Output() addFiles = new EventEmitter<FileModel[]>();
    @Output() continue = new EventEmitter<NameAndSelectedFilesContinueInformation>();
    @Output() removeFiles = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();
    @Output() selectedFilesUpdatedBecauseOfUpload = new EventEmitter<FileModel[]>();
    @Output() setNewModalSize = new EventEmitter<string>();
    @Output() proceedButtonStateChange = new EventEmitter<boolean>();
    @Output() cziConversionInProgress = new EventEmitter<boolean>();

    get parentFolderName(): string {
        return this._parentFolderName;
    }

    get selectedFiles(): FileModel[] {
        return this._selectedFiles;
    }

    get supportedFileTypes(): string[] {
        return this._supportedFileTypes;
    }

    get isNameFormValid(): boolean {
        return this.nameForm?.form?.valid;
    }

    constructor(
        private activeModal: NgbActiveModal,
        private createAndStartFacadeService: CreateAndStartFacadeService,
        private cziConverterService: CziConverterService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private filePreviewService: FilePreviewService,
        private filesValidatorService: FilesValidatorService,
        private fileUploadService: FileUploadService,
        private messagingService: MessagingService,
        private userNotificationService: UserNotificationService,
        private mixpanelTrackingService: TrackingService,
        private userService: UserService,
        private ngbModal: NgbModal,
        private windowRef: WindowRef,
    ) {
        this.messagingService
            .subscription(this.messagingTopicId)
            .pipe(
                mergeMap(
                    (message: Message<Dataset | FileModel>): Observable<string | Message<Dataset | FileModel>> =>
                        iif(
                            (): boolean => message.action === CZI_CONVERSION_DONE_NEXT_ANNOTATE_DATASET && !!message.payload,
                            of(message).pipe(
                                switchMap((message: Message<Dataset | FileModel>): Observable<string> => {
                                    const currentPreselectedDataset = message.payload as Dataset;
                                    return this.filePreviewService
                                        .generateArivisMachineLearningViewerUrlForDataset$(
                                            currentPreselectedDataset.datasetId,
                                            currentPreselectedDataset.userId,
                                        )
                                        .pipe(
                                            tap((url: string): void => {
                                                this.windowRef.nativeWindow.open(url, '_blank');
                                                this.dismiss();
                                            }),
                                            untilDestroyed(this),
                                        );
                                }),
                            ),
                            of(message).pipe(
                                tap((message: Message<Dataset | FileModel>): void => {
                                    if (
                                        message.action === CZI_CONVERSION_REMOVED_FAILED_CONVERSION_FILE &&
                                        message.payload &&
                                        this.datasetExplorerFolderHandlerType === DatasetFileTypeOption.FILES
                                    ) {
                                        this.currentPreselectedDataset.statistics.numberOfFiles += 1;
                                        this.currentPreselectedDataset.statistics.numberOfCziFiles += FileUtils.getNumberOfCziFiles([
                                            message.payload as FileModel,
                                        ]);
                                    }
                                }),
                            ),
                        ),
                ),

                untilDestroyed(this),
            )
            .subscribe();
    }
    /**
     * Lifecycle hook that is called when the component is initialized.
     * This method initializes the `isFileExplorerShown` state based on whether files are selected,
     * and subscribes to a service to update the status of selected files.
     *
     * It calls the `cziConverterService.updateOperationFileStatuses$` method to retrieve the latest file statuses
     * for the current dataset and updates the `selectedFiles` array accordingly.
     *
     * @returns {void}
     */
    ngOnInit(): void {
        this.isFileExplorerShown = this.isSelectedFilesShown ? true : false;
        this.updateFileStatuesAction$$
            .pipe(
                switchMap(
                    (): Observable<FileModel[]> =>
                        this.cziConverterService.updateOperationFileStatuses$(this.currentPreselectedDataset?.datasetId, this.selectedFiles).pipe(
                            tap(files => {
                                this.selectedFiles = [...files];
                            }),
                        ),
                ),
                untilDestroyed(this),
            )
            .subscribe();
    }

    /**
     * Lifecycle hook that is called after the view has been initialized.
     * This method is used to avoid the "expression has changed after it was checked" exception by using `setTimeout`.
     * It fetches the file upload cache for the operation and assigns it to the `uploader` property.
     *
     * @returns {void}
     */
    ngAfterViewInit(): void {
        // setTimeout to avoid expression has changed after it was checked exception
        setTimeout((): void => {
            this.uploader = this.fileUploadService.fetchFromCache(`selectedFiles_${this.operationId}`);
        });
    }

    /**
     * Emits the progress status of the CZI conversion process.
     * This method is used to notify whether the CZI conversion process is in progress.
     *
     * @param isInProgress - A boolean indicating whether the CZI conversion is in progress (`true`) or not (`false`).
     */
    handleCziConversionInProgressChange(isInProgress: boolean): void {
        this.cziConversionInProgress.emit(isInProgress);
    }

    handleValidationInProgressChange(isInProgress: boolean): void {
        this.isValidationInProgress = isInProgress;
    }

    /**
     * Initiates the process of proceeding with the dataset upload and handling the related actions.
     * Optionally tracks the event in Mixpanel, checks folder existence and renames if necessary, and emits a continue event.
     *
     * @param mixpanelEvent - A boolean flag indicating whether to track the event in Mixpanel (`true`) or not (`false`).
     */
    proceed(mixpanelEvent: boolean = false): void {
        if (mixpanelEvent) {
            const userId = this.userService.myInfo?.sub;
            this.mixpanelTrackingService.trackEvent('open_dataset', { userId, datasetId: this.currentPreselectedDataset.datasetId });
        }

        iif(
            (): boolean => this.shouldCheckIfNameExistsAsFolderAndRenameFolder && this.uploadFolder != null && !this.isSegmentation,
            this.createAndStartFacadeService.checkFolderAndRenameTemporaryFolderToRunName$(this.uploadFolder, this.parentFolderName, this.name.trim()),
            of(undefined),
        )
            .pipe(
                tap((): void => {
                    this.continue.emit({
                        resultName: this.name.trim(),
                        files: this.selectedFiles,
                        uploadFolder: this.uploadFolder,
                        closeDialog: (): void => this.activeModal.close(),
                    });
                }),
                catchError((error: unknown): Observable<never> => {
                    console.error('Error in proceed method:', error);
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    /**
     * Dismisses the current modal dialog.
     * This method is used to close the modal and discard any changes made in the modal window.
     */
    dismiss(): void {
        this.activeModal.dismiss();
    }

    /**
     * Determines if the "Proceed" button should be disabled.
     * @returns True if the button should be disabled, false otherwise.
     */
    isProceedButtonDisabled(): boolean {
        if (this.isSegmentation && this.shouldContinueButtonBeValidated) {
            const hasInvalidFiles = this.selectedFiles.some(
                file =>
                    (file.convertStatus === 'Success' && FileUtils.hasInvalidValidation(file.validationStatus)) ||
                    FileUtils.hasFailedConversion(file.convertStatus),
            );

            const isNextDisabled =
                this.selectedFiles.some((file: FileModel): boolean => file.convertStatus === 'Active') ||
                this.selectedFiles.some((file: FileModel): boolean => file.validationStatus === 'PendingValidation') ||
                (this.uploader?.queue.length !== undefined && this.uploader?.queue.length !== 0) ||
                this.selectedFiles.length === 0 ||
                FileUtils.containsPendingCZIConversion(this.selectedFiles) ||
                (this.shouldCheckValidityOfName ? !this.isNameFormValid : false);

            const isStartDisabled = isNextDisabled || hasInvalidFiles; // Add invalid file check for the "Start" button

            this.proceedButtonStateChange.emit(isStartDisabled as boolean | undefined);

            return isNextDisabled;
        }

        // Original conditions for non-segmentation cases (datasets, application runs or segmentation which does not contain invalid files)
        if (this.shouldContinueButtonBeValidated) {
            if (this.isMEDProjectEnabled) {
                return (
                    this.selectedFiles.some((file: FileModel): boolean => file.convertStatus === 'Active') ||
                    this.selectedFiles.some((file: FileModel): boolean => file.validationStatus === 'PendingValidation') ||
                    this.selectedFiles.length === 0 ||
                    (this.shouldCheckValidityOfName ? !this.isNameFormValid : false)
                );
            }

            return (
                this.selectedFiles.some((file: FileModel): boolean => file.convertStatus === 'Active') ||
                this.selectedFiles.some((file: FileModel): boolean => file.validationStatus === 'PendingValidation') ||
                this.uploader?.queue.length !== 0 ||
                this.selectedFiles.length === 0 ||
                (this.currentPreselectedDataset ? !FileUtils.containsOnlyValidDatasetFile(this.selectedFiles) : false) ||
                (this.shouldCheckValidityOfName ? !this.isNameFormValid : false) ||
                (this.experimentalFeaturesService.isCziConversionFeatureEnabled
                    ? this.isCziConversionActivatedForUploadedFiles && !this.areAllSelectedFilesOfTypeCZI
                    : false)
            );
        }

        return false;
    }

    /**
     * Handles the "Proceed" button click for invalid segmentation files.
     */
    handleProceedClick(): void {
        if (this.isSegmentation && !FileUtils.containsOnlyValidSegmentationFiles(this.selectedFiles)) {
            this.openHandleInvalidFilesModal();
        } else {
            this.submitForm();
        }
    }

    /**
     * Updates the search and sort options for file selection.
     * This method is called when the search or sorting parameters change, updating the internal state.
     *
     * @param searchParams - An object containing the search and sort parameters to be applied.
     */
    onChangeSearchAndSortOptions(searchParams: ListSearch): void {
        this.searchAndSortOptionsForFileSelection = { ...searchParams };
    }

    /**
     * Updates the selected files and checks if the proceed button should be enabled or disabled.
     * This method is called when the list of selected files changes, and it triggers the update of the proceed button state.
     *
     * @param files - An array of `FileModel` objects representing the newly selected files.
     */
    onSelectedFilesUpdated(files: FileModel[]): void {
        this.selectedFiles = [...files];
        this.isProceedButtonDisabled();
        this.selectedFilesUpdatedBecauseOfUpload.emit(this.selectedFiles);
    }

    /**
     * Updates the uploaded folder after it has been created.
     * This method is called when a new folder is successfully created for the upload.
     *
     * @param folder - The `Folder` object representing the newly created folder.
     */
    onTargetFolderCreated(folder: Folder): void {
        this.uploadFolder = folder;
    }

    /**
     * Validates the name form control and marks it as dirty if the value is not provided.
     * This method is used to ensure that the name field is not empty and visually highlights it if necessary.
     */
    validate(): void {
        const nameFormControl: AbstractControl = this.nameForm.controls.name;
        if (!nameFormControl.value) {
            nameFormControl.markAsDirty();
        }
    }

    /**
     * Handles the file selection event and validates the selected files.
     * This method checks the validity of the files selected by the user and emits valid files for further processing.
     *
     * @param files - The array of `FileModel` objects representing the files selected by the user.
     * Each file is validated for type, size, and name restrictions.
     *
     * The method uses the `filesValidatorService` to determine which files are valid and which are invalid.
     * Valid files are then emitted for further processing.
     */
    onFilesSelected(files: FileModel[]): void {
        this.filesValidatorService
            .getValidAndInvalidFiles$<FileModel>(
                [...files],
                this.selectedFiles,
                this.filesValidatorService.getCurrentlyUploadingFiles(this.uploader?.queue || [], files.length),
                {
                    errorNotificationMode: 'notification',
                    isFileTypeSupportedOptions: { supportedFileTypes: this.supportedFileTypes },
                    isMaxFileSizeExceededOptions: { maxInputSizeInBytes: this.uploadMaxFileSizeInBytes },
                    checkFileNameOptions: { allowSameFileNames: false, checkSingleFileNameForInvalidCharacters: true },
                },
            )
            .pipe(
                tap((validAndInvalidFiles: ValidAndInvalidFiles<FileModel>): void => {
                    if (validAndInvalidFiles.validFiles.length > 0) {
                        this.addFiles.emit([...validAndInvalidFiles.validFiles]);
                    }
                }),
                take(1),
            )
            .subscribe();
    }

    /**
     * Opens a modal to handle invalid files, allowing the user to review and remove them.
     * Once the modal is closed, emits an event to remove the invalid files without confirmation
     * and shows a success notification.
     */
    openHandleInvalidFilesModal(): void {
        const modalRef = this.ngbModal.open(RemoveInvalidFilesDialogComponent, { centered: true, size: 'lg' });
        const invalidFiles = this.selectedFiles.filter(
            file =>
                (file.convertStatus === 'Success' && FileUtils.hasInvalidValidation(file.validationStatus)) ||
                FileUtils.hasFailedConversion(file.convertStatus),
        );

        modalRef.componentInstance.invalidFiles = invalidFiles;
        modalRef.componentInstance.isSegmentation = this.isSegmentation;

        modalRef.closed
            .pipe(
                tap((): void => {
                    this.removeFiles.emit({
                        items: invalidFiles,
                        shouldConfirmDialogBeOpened: false,
                    });

                    if (invalidFiles.length === 1) {
                        this.userNotificationService.showSuccess('MY_WORKSPACE.MY_FILES.FILE_DELETED_SUCCESSFULLY');
                    } else if (invalidFiles.length > 1) {
                        this.userNotificationService.showSuccess('MY_WORKSPACE.MY_FILES.FILES_DELETED_SUCCESSFULLY');
                    }
                }),
                catchError((error: unknown): Observable<null> => {
                    if (invalidFiles.length === 1) {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.FILE_COULD_NOT_BE_DELETED');
                    } else if (invalidFiles.length > 1) {
                        this.userNotificationService.showError('MY_WORKSPACE.MY_FILES.FILES_COULD_NOT_BE_DELETED');
                    }

                    console.error('Error removing files:', error);
                    return of(null); // Return a safe observable to continue the stream and allow the untilDestroy to clean the subscription properly
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    /**
     * Opens the file selection section and emits an event to set the modal size.
     * This method makes the file explorer visible and triggers a change in modal size for uploading and selecting files.
     */
    openSelectionSection(): void {
        this.isFileExplorerShown = true;
        this.setNewModalSize.emit('upload-and-select-files');
    }

    /**
     * Opens the dialog displaying the supported file formats.
     * This method opens a modal to show users the supported formats for file uploads.
     */
    openSupportedFormatsDialog(): void {
        this.ngbModal.open(SupportedFormatsDialogComponent, { size: 'upload-files', centered: true });
    }

    /**
     * Submits the form and optionally sends a Mixpanel event.
     * If `mixpanelEvent` is true, it tracks the submission event.
     *
     * @param mixpanelEvent - A boolean to indicate whether to track the event with Mixpanel. Default is `false`.
     */
    submitForm(mixpanelEvent: boolean = false): void {
        this.proceed(mixpanelEvent);
    }

    /**
     * Checks if all the files in the provided array are of type CZI.
     * It compares each file's type or its extension to the CZI extension.
     *
     * @param files - An array of `FileModel` objects to check.
     * @returns True if all files are of type CZI, otherwise false.
     */
    private areAllFilesOfTypeCZI(files: FileModel[]): boolean {
        return files.length > 0 && files.every(f => (f.type ? f.type : FileUtils.getFileExtension(f.token)) === CZI_EXTENSION);
    }
}
